/**
 * Schémas Zod — Public intake (Chunk 9.N.1.B — GET metadata).
 *
 * Surface 9.N.1.B (publique, no-auth) :
 *   - GET /v1/public/intake/:token  (metadata + status)
 *
 * Hors scope 9.N.1.B (déféré 9.N.1.B2) :
 *   - POST /v1/public/intake/:token (multipart upload + storage + intake_events)
 *
 * Cf. cadrage F2 §3.10 (`docs/architecture/F2-public-intake-cadrage.md`).
 */

import { z } from "zod";

// -----------------------------------------------------------------------------
// Query — ?lang=fr|en optionnel.
//   Chaîne de détection (§3.6 cadrage) : ?lang > Accept-Language > "fr".
//   Toute autre valeur de ?lang est ignorée (pas une 400 — silence + fallback).
// -----------------------------------------------------------------------------

export const PublicIntakeLanguageSchema = z.enum(["fr", "en"]);
export type PublicIntakeLanguage = z.infer<typeof PublicIntakeLanguageSchema>;

export const PublicIntakeQuerySchema = z.object({
  lang: PublicIntakeLanguageSchema.optional(),
});
export type PublicIntakeQuery = z.infer<typeof PublicIntakeQuerySchema>;

// -----------------------------------------------------------------------------
// Limits — cadrage F2 §3.5.
//
// Constantes exposées dans la réponse de succès pour que la FE 9.N.2 connaisse
// les bornes côté client (dropzone fichiers + validation pré-POST). Réutilisées
// par 9.N.1.B2 lors du POST upload pour valider serveur-side (single source
// of truth pour les limites).
// -----------------------------------------------------------------------------

export const PublicIntakeLimitsSchema = z.object({
  max_files: z.number().int().min(1).max(20),
  max_size_mb_per_file: z.number().int().min(1).max(500),
  max_total_mb: z.number().int().min(1).max(2000),
  mime_types: z.array(z.string().min(1)),
});
export type PublicIntakeLimits = z.infer<typeof PublicIntakeLimitsSchema>;

// -----------------------------------------------------------------------------
// Response — succès (status: "valid")
//
// Anti-leak strict (cadrage §3.10 "pas de fuite tenant") :
//   - tenant_id, tenant_slug, tenant_name : NON exposés.
//   - project_id, broker email/nom        : NON exposés.
//   - expires_at, revoked_at, submission_count, max_submissions : NON exposés.
//
// Champs exposés strictement nécessaires au rendu de la page publique :
//   - project.title : choix opérationnel — broker a délibérément partagé le
//                     lien, donc le titre projet est partagé volontairement.
//                     Si le broker veut anonymiser, qu'il nomme son projet
//                     "Demande client" (UX side, pas API).
//   - language      : pour rendu i18n FE.
//   - limits        : pour validation FE pré-POST 9.N.1.B2.
// -----------------------------------------------------------------------------

/**
 * Les valeurs que le serveur SUGGÈRE pour amorcer un formulaire — chunk 9.BJ.3.
 *
 * ## Ce qui voyage, et rien d'autre
 *
 * Une carte plate `{ clé de champ → valeur }`, et **uniquement pour les champs
 * que le gabarit déclare** avec `prefill_from`. Aucune structure
 * entreprise/contact/conseiller, aucun identifiant interne, aucune donnée
 * ajoutée par commodité. C'est le cabinet qui décide, dans son propre gabarit,
 * ce qu'un porteur de jeton peut voir.
 *
 * ## Suggérer n'est pas imposer
 *
 * Le GET suggère ; le POST **ne réinjecte jamais** ces valeurs. Ce qui est
 * enregistré — et signé — est exactement ce que la personne a soumis, y compris
 * si elle a corrigé la suggestion. Sans quoi on ferait signer autre chose que ce
 * qui était à l'écran.
 *
 * Une source indisponible ne produit **aucune entrée** : pas de repli, pas de
 * valeur devinée. Un champ à remplir vaut mieux qu'un nom erroné.
 */
export const PublicIntakePrefillSchema = z.record(z.string(), z.string());
export type PublicIntakePrefill = z.infer<typeof PublicIntakePrefillSchema>;

export const PublicIntakeMetadataSchema = z.object({
  status: z.literal("valid"),
  project: z.object({
    title: z.string().min(1),
  }),
  language: PublicIntakeLanguageSchema,
  limits: PublicIntakeLimitsSchema,
});
export type PublicIntakeMetadata = z.infer<typeof PublicIntakeMetadataSchema>;
